/**
 * The session controls — mode, feedback and language — shared by the studio and the player.
 *
 * One component, so the two hosts this repository writes cannot drift on what a session offers or
 * how it is changed. Two pieces, placed by the caller wherever its toolbar has room:
 *
 *   `chip`    what the session IS at a glance ("Practice · feedback on check"), and the button
 *             that opens the panel to change it. Mode and feedback are chosen together and applied
 *             once, because each change re-initialises the asset and the two are not independent —
 *             exam narrows what feedback can be.
 *   `locale`  the asset's declared languages as a pill toggle, applied on click.
 *
 * Explanations are tooltips, not paragraphs: the panel is for choosing, and the person choosing
 * knows what the words mean most of the time.
 *
 * Styled through the CSS variables both apps already define (`--accent`, `--ink`, `--line`, …),
 * with fallbacks, and its own `sc-` class names so it carries no assumption about either page.
 */

import type { FeedbackMode, Mode } from '@cet/asset-contract';

import { FEEDBACK_BY_MODE, FEEDBACK_LABEL, isReadOnly, wireFeedbackMode, wireLocale } from './policy.js';
import type { HostRecord } from './policy.js';

export interface SessionControlsOptions {
  /** The record whose `mode`, `feedbackMode` and `locale` these controls set. */
  readonly record: HostRecord;
  /** `manifest.locales` of the asset in the frame. */
  locales(): readonly string[];
  /** False while no asset is live: every control here re-initialises one. */
  enabled(): boolean;
  /**
   * A setting was applied. `reinit` is false when nothing on the wire moved — picking, in exam, the
   * timing exam already falls back to — and re-initialising would throw away an attempt to apply
   * nothing.
   */
  onChange(change: { readonly why: string; readonly reinit: boolean }): void;
}

export interface SessionControls {
  readonly chip: HTMLElement;
  readonly locale: HTMLElement;
  /** Redraw from the record. Call after anything else changes it — a submit, a reset. */
  render(): void;
  close(): void;
}

const MODES: readonly Mode[] = ['practice', 'exam', 'review', 'solution'];
const FEEDBACKS: readonly FeedbackMode[] = ['onAnswer', 'onCheck', 'onSubmit', 'none'];

const MODE_TIP: Record<Mode, string> = {
  practice: 'The answer key is delivered; Feedback decides when marks appear.',
  exam: 'The answer key is withheld. Feedback is On submit or None.',
  review: 'A read-only replay of a finished attempt.',
  solution: 'The correct answers, and nothing about a learner.',
};

const FEEDBACK_TIP: Record<FeedbackMode, string> = {
  onAnswer: 'Each answer is marked as it is given.',
  onCheck: 'Marked when the learner presses Check.',
  onSubmit: 'Marked at Submit, which ends the attempt.',
  none: 'Never marked in this session, submit included.',
};

const LOCALE_SHORT: Record<string, string> = { he: 'עב', en: 'EN', ar: 'ع' };
const LOCALE_NAME: Record<string, string> = { he: 'עברית', en: 'English', ar: 'العربية' };

const capital = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1);

export function createSessionControls(options: SessionControlsOptions): SessionControls {
  injectStyles();
  const { record } = options;

  const make = <K extends keyof HTMLElementTagNameMap>(tag: K, className: string, text = ''): HTMLElementTagNameMap[K] => {
    const element = document.createElement(tag);
    element.className = className;
    if (text !== '') element.textContent = text;
    return element;
  };
  const button = (className: string, text: string): HTMLButtonElement => {
    const element = make('button', className, text);
    element.type = 'button';
    return element;
  };

  // ------------------------------------------------------------- chip
  const chip = make('div', 'sc-chip');
  chip.dataset['sessionChip'] = '';
  const dot = make('span', 'sc-dot');
  dot.setAttribute('aria-hidden', 'true');
  const modeText = make('span', 'sc-mode');
  const detail = make('span', 'sc-detail');
  const change = button('sc-change', 'Change');
  change.setAttribute('aria-haspopup', 'dialog');
  change.setAttribute('aria-expanded', 'false');
  chip.append(dot, modeText, detail, change);

  // ------------------------------------------------------------- panel
  const panel = make('div', 'sc-panel');
  panel.setAttribute('role', 'dialog');
  panel.setAttribute('aria-label', 'Session');
  panel.hidden = true;
  const modeGroup = make('div', 'sc-segmented');
  modeGroup.setAttribute('role', 'radiogroup');
  modeGroup.setAttribute('aria-label', 'Mode');
  const feedbackGroup = make('div', 'sc-segmented');
  feedbackGroup.setAttribute('role', 'radiogroup');
  feedbackGroup.setAttribute('aria-label', 'Feedback');
  const narrowed = make('p', 'sc-note');
  const apply = button('sc-apply', 'Apply');
  const foot = make('div', 'sc-foot');
  foot.append(make('span', 'sc-warn', 'Applying restarts the session.'), apply);
  panel.append(
    make('div', 'sc-label', 'Mode'),
    modeGroup,
    make('div', 'sc-label', 'Feedback'),
    feedbackGroup,
    narrowed,
    foot,
  );
  document.body.append(panel);

  // ------------------------------------------------------------- locale
  const locale = make('div', 'sc-langs');
  locale.setAttribute('role', 'radiogroup');
  locale.setAttribute('aria-label', 'Language');

  /** What the panel shows before Apply — the record changes only when it is applied. */
  let pending = { mode: record.mode, feedbackMode: record.feedbackMode };

  function renderPanel(): void {
    modeGroup.replaceChildren(
      ...MODES.map((value) => {
        const choice = button('', capital(value));
        choice.setAttribute('role', 'radio');
        choice.setAttribute('aria-checked', String(value === pending.mode));
        choice.dataset['mode'] = value;
        choice.title = MODE_TIP[value];
        choice.addEventListener('click', () => {
          pending = { ...pending, mode: value };
          renderPanel();
        });
        return choice;
      }),
    );

    const allowed = FEEDBACK_BY_MODE[pending.mode];
    const effective = wireFeedbackMode(pending);
    feedbackGroup.replaceChildren(
      ...FEEDBACKS.map((value) => {
        const choice = button('', capital(FEEDBACK_LABEL[value]));
        choice.setAttribute('role', 'radio');
        choice.setAttribute('aria-checked', String(value === effective));
        choice.dataset['feedback'] = value;
        // Disabled rather than hidden: what is worth seeing is that the mode narrows the choice.
        choice.disabled = !allowed.includes(value);
        choice.title = choice.disabled ? `${capital(pending.mode)} cannot send this.` : FEEDBACK_TIP[value];
        choice.addEventListener('click', () => {
          pending = { ...pending, feedbackMode: value };
          renderPanel();
        });
        return choice;
      }),
    );
    narrowed.textContent =
      allowed.length === 0
        ? `${capital(pending.mode)} sends no timing.`
        : allowed.length < FEEDBACKS.length
          ? `${capital(pending.mode)} allows only ${allowed.map((value) => capital(FEEDBACK_LABEL[value])).join(' or ')}.`
          : '';
    narrowed.hidden = narrowed.textContent === '';
  }

  function place(): void {
    const box = chip.getBoundingClientRect();
    const width = Math.min(440, window.innerWidth - 16);
    const left = Math.max(8, Math.min(box.left + box.width / 2 - width / 2, window.innerWidth - width - 8));
    panel.style.width = `${width}px`;
    panel.style.left = `${left}px`;
    panel.style.top = `${box.bottom + 8}px`;
  }

  function open(next: boolean): void {
    if (next) {
      pending = { mode: record.mode, feedbackMode: record.feedbackMode };
      renderPanel();
      place();
    }
    panel.hidden = !next;
    chip.dataset['open'] = String(next);
    change.setAttribute('aria-expanded', String(next));
    if (next) modeGroup.querySelector<HTMLButtonElement>('[aria-checked="true"]')?.focus();
  }

  function applyPending(): void {
    const before = { mode: record.mode, wire: wireFeedbackMode(record) };
    const { mode, feedbackMode } = pending;
    open(false);
    change.focus();
    if (mode === record.mode && feedbackMode === record.feedbackMode) return;
    record.mode = mode;
    record.feedbackMode = feedbackMode;
    const wire = wireFeedbackMode(record);
    render();
    if (mode === before.mode && wire === before.wire) {
      options.onChange({
        why: `feedbackMode → ${feedbackMode} · ${mode} already sends ${wire ?? 'no timing'}, so no re-init`,
        reinit: false,
      });
      return;
    }
    options.onChange({ why: `session → ${mode} · ${wire ?? 'no timing'} · the host re-inits`, reinit: true });
  }

  function renderLocales(): void {
    const declared = options.locales();
    const current = wireLocale(record, declared);
    const live = options.enabled();
    locale.replaceChildren(
      ...(declared.length > 0 ? declared : [current]).map((value) => {
        const choice = button('', LOCALE_SHORT[value] ?? value.toUpperCase());
        choice.setAttribute('role', 'radio');
        choice.setAttribute('aria-checked', String(value === current));
        choice.setAttribute('aria-label', LOCALE_NAME[value] ?? value);
        choice.title = LOCALE_NAME[value] ?? value;
        choice.lang = value;
        choice.dataset['locale'] = value;
        // One language is still shown — it says what the asset is playing in — but there is
        // nothing to choose, so it is not a control.
        choice.disabled = !live || declared.length < 2;
        choice.addEventListener('click', () => {
          if (value === wireLocale(record, options.locales())) return;
          record.locale = value;
          render();
          options.onChange({ why: `locale → ${value} · the host re-inits`, reinit: true });
        });
        return choice;
      }),
    );
  }

  function render(): void {
    const kind =
      isReadOnly(record) ? 'closed' : record.mode === 'exam' ? 'exam' : 'practice';
    dot.dataset['kind'] = kind;
    modeText.textContent = capital(record.mode);
    const wire = wireFeedbackMode(record);
    detail.textContent =
      record.mode === 'review'
        ? '· replaying a finished attempt'
        : record.mode === 'solution'
          ? '· answers shown'
          : record.submitted
            ? '· submitted, read-only'
            : wire === null
              ? ''
              : `· feedback ${FEEDBACK_LABEL[wire]}`;
    change.disabled = !options.enabled();
    renderLocales();
    if (!panel.hidden) renderPanel();
  }

  change.addEventListener('click', () => open(panel.hidden));
  apply.addEventListener('click', applyPending);
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !panel.hidden) {
      open(false);
      change.focus();
    }
  });
  // The path, not `contains(target)`: choosing a segment redraws the group, so by the time the
  // click bubbles here its target is detached and would read as a click outside the panel.
  document.addEventListener('click', (event) => {
    const path = event.composedPath();
    if (!panel.hidden && !path.includes(panel) && !path.includes(chip)) open(false);
  });
  window.addEventListener('resize', () => {
    if (!panel.hidden) place();
  });

  render();
  return { chip, locale, render, close: () => open(false) };
}

// ---------------------------------------------------------------- styles

let injected = false;

/** Injected once, so the component is one import and not an import plus a stylesheet to remember. */
function injectStyles(): void {
  if (injected) return;
  injected = true;
  const style = document.createElement('style');
  style.dataset['cet'] = 'session-controls';
  style.textContent = `
.sc-chip{display:inline-flex;align-items:center;gap:8px;padding:4px 4px 4px 12px;border:1px solid var(--line,#d7dee6);border-radius:999px;background:var(--sunken,#f5f8fb);color:var(--ink,#131c26);white-space:nowrap;font:inherit}
.sc-chip[data-open=true]{border-color:var(--accent,#15619f);background:var(--accent-soft,#e2eef8)}
.sc-dot{width:8px;height:8px;border-radius:50%;background:var(--good,#12693e);flex:none}
.sc-dot[data-kind=exam]{background:var(--warn,#8a5300)}
.sc-dot[data-kind=closed]{width:14px;height:14px;border-radius:0;background:var(--ink-2,#4a5b6d);-webkit-mask:var(--sc-lock) center/contain no-repeat;mask:var(--sc-lock) center/contain no-repeat}
.sc-chip,.sc-panel{--sc-lock:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='5' y='11' width='14' height='10' rx='2'/%3E%3Cpath d='M8 11V7a4 4 0 0 1 8 0v4'/%3E%3C/svg%3E")}
.sc-mode{font-weight:600}
.sc-detail{color:var(--ink-2,#4a5b6d)}
.sc-change{font:inherit;cursor:pointer;border:0;background:var(--surface,#fff);color:var(--ink,#131c26);border-radius:999px;padding:6px 12px;box-shadow:0 0 0 1px var(--line,#d7dee6)}
.sc-chip[data-open=true] .sc-change{background:var(--accent,#15619f);color:var(--accent-on,#fff);box-shadow:none}
.sc-change:disabled{opacity:.45;cursor:not-allowed}
.sc-panel{position:fixed;z-index:1000;display:flex;flex-direction:column;gap:10px;padding:18px;box-sizing:border-box;background:var(--surface,#fff);color:var(--ink,#131c26);border:1px solid var(--line,#d7dee6);border-radius:12px;box-shadow:0 12px 32px rgba(19,28,38,.18);font:14px/1.4 var(--sans,inherit)}
.sc-panel[hidden]{display:none}
.sc-label{font-weight:600;margin-top:4px}
.sc-segmented{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:4px;padding:4px;border-radius:10px;background:var(--page,#eef2f6)}
.sc-segmented button{font:inherit;height:36px;padding:0 6px;border:0;border-radius:7px;background:transparent;color:var(--ink-2,#4a5b6d);cursor:pointer;white-space:nowrap}
.sc-segmented button[aria-checked=true]{background:var(--surface,#fff);color:var(--ink,#131c26);font-weight:600;box-shadow:0 1px 2px rgba(19,28,38,.15)}
.sc-segmented button:disabled{opacity:.4;cursor:not-allowed}
.sc-note{margin:0;color:var(--ink-2,#4a5b6d);font-size:13px}
.sc-foot{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-top:6px}
.sc-warn{color:var(--warn,#8a5300);font-size:13px}
.sc-apply{font:inherit;font-weight:600;cursor:pointer;height:38px;padding:0 18px;border:0;border-radius:8px;background:var(--accent,#15619f);color:var(--accent-on,#fff)}
.sc-langs{display:inline-flex;gap:2px;padding:3px;border-radius:999px;background:var(--page,#eef2f6);box-shadow:inset 0 0 0 1px var(--line,#d7dee6)}
.sc-langs button{font:inherit;font-weight:600;min-width:40px;height:32px;padding:0 10px;border:0;border-radius:999px;background:transparent;color:var(--ink-2,#4a5b6d);cursor:pointer}
.sc-langs button[aria-checked=true]{background:var(--surface,#fff);color:var(--ink,#131c26);box-shadow:0 1px 2px rgba(19,28,38,.18)}
.sc-langs button:disabled{cursor:default}
.sc-langs button:disabled:not([aria-checked=true]){opacity:.45}
.sc-chip :focus-visible,.sc-panel :focus-visible,.sc-langs :focus-visible{outline:2px solid var(--accent,#15619f);outline-offset:2px}
`;
  document.head.append(style);
}
